import { ReactNode } from "react"
import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import Dialog from "../chakra/Dialog"
import { ActionButton } from "../content/components/Button"
import { getConnectorConfig } from "./constants"

export interface DeleteDataConnectionContext {
  connectorName: string
  connectorType?: string
  externalId?: string | null
  siteName?: string | null
  assetNames?: string[]
}

interface DeleteDataConnectionDialogProps {
  open: boolean
  context: DeleteDataConnectionContext
  onConfirm: () => void
  onClose: () => void
  loading?: boolean
  /** Dialog heading. A system and an asset word the removal differently. */
  title: string
  /**
   * What removal will do, in the caller's own words. A system loses a link to
   * a data source; an asset loses its source mapping and stops being
   * monitored. Neither sentence is true of the other, so neither belongs here.
   */
  consequences: ReactNode
}

export function DeleteDataConnectionDialog({
  open,
  context,
  onConfirm,
  onClose,
  loading = false,
  title,
  consequences,
}: DeleteDataConnectionDialogProps) {
  const { connectorName, connectorType, externalId, siteName, assetNames } = context
  const hasDetails = siteName || externalId || (assetNames && assetNames.length > 0)
  const config = getConnectorConfig(connectorType ?? "")

  return (
    <Dialog
      title={title}
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) onClose()
      }}
      onClose={onClose}
      footer={
        <HStack gap={3}>
          <ActionButton
            onClick={onClose}
            disabled={loading}
            context={{
              label: "Cancel",
              description: "Cancel Deletion",
              variant: "secondary",
            }}
          />
          <ActionButton
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
            context={{
              label: "Delete",
              description: "Remove this data connection",
              variant: "warning",
            }}
          />
        </HStack>
      }
    >
      <VStack align="stretch" gap={3}>
        {hasDetails && (
          <Box borderWidth="1px" borderRadius="md" p={3} bg="bg.subtle">
            <VStack align="stretch" gap={1}>
              <Text fontWeight="semibold" fontSize="sm">{connectorName}</Text>
              {externalId && (
                <Text fontSize="sm" color="fg.muted">{config.externalIdLabel}: {externalId}</Text>
              )}
              {siteName && (
                <Text fontSize="sm" color="fg.muted">Site: {siteName}</Text>
              )}
              {assetNames && assetNames.length > 0 && (
                <Text fontSize="sm" color="fg.muted">
                  Assets: {assetNames.join(", ")}
                </Text>
              )}
            </VStack>
          </Box>
        )}
        {consequences}
      </VStack>
    </Dialog>
  )
}

export default DeleteDataConnectionDialog
